/**
 * TimesheetDayEditor — единая **дневная оболочка** правки времени сотрудника
 * (задача 79 §2.1). Тело без контейнера: записи дня + табель + финальное
 * действие. Контейнер (правый `<Sheet>` дока или инлайн backfill-страницы)
 * принадлежит хосту — оболочка рендерит только тело.
 *
 * Состав (всё **ниже** селектора сотрудника, идентичен на обеих поверхностях):
 *  - контекст-шапка (имя · день · статус — формат дат `formatWeekdayDate`, файл 22 §4.6);
 *  - список записей (`EntryList`) + «Добавить запись» → единый редактор
 *    (`AdminEntryEditor`, `surface="desktop-dock"`, задача 77 §4.1);
 *  - стэппер **ТАБЕЛЬ** (`DesktopHoursInput` + `OvertimeBanner`, из `SignBar`);
 *  - финал: self — «Подписать» через `useSignAdminTimesheet`; чужой отчёт —
 *    «Подтвердить» = табель + approve одним действием (задача 175 §1.1).
 *
 * Паттерн — `patterns.md §Рабочее место руководителя` (дневная оболочка теперь
 * `TimesheetDayEditor`, общая для дока и backfill) + `§Единый редактор записи
 * времени` (редактор записи — переиспользуем, не трогаем).
 *
 * Снапшот тянем **внутри** компонента через общий `useAdminTimesheet`, чтобы
 * оболочки не расходились. Surface-флаг (`mobile | desktop-dock`, задача 79 §2.2)
 * рулит density-токенами через `data-entry-surface` на корне тела: финал-кнопка,
 * стэппер ТАБЕЛЬ (`DesktopHoursInput`) и прочие интерактивы
 * (правка/удаление записи, «Добавить запись») читают `--entry-tap-*` —
 * крупные тач-таргеты на мобильном (M6 `/field/lead`), компактные на desktop-dock.
 * Контейнер (fullscreen ↔ правый `<Sheet>`) выбирает **хост** (`EntryDock`) по
 * surface — оболочка рендерит только тело, 1-в-1 как `EntryEditorCore` (задача 77).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DesktopHoursInput } from "@/components/admin/timesheet/DesktopHoursInput";
import { OvertimeBanner } from "@/components/timesheet/OvertimeBanner";
import { ReworkBadge } from "@/components/timesheet/ReworkBadge";
import { ConfirmDialog } from "@/components/templates/ConfirmDialog";
import {
  AdminEntryEditor,
  type AdminEntryTarget,
} from "@/components/field/entry-editor/AdminEntryEditor";
import { formatHoursHalf } from "@/lib/timesheet";
import { formatWeekdayDate } from "@/lib/datetime";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import { showApiErrorToast } from "@/lib/api-error-toast";
import {
  FormErrorSummary,
  FormFieldError,
  apiErrorFormIssue,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";
import {
  useAdminEntryWorkTypes,
  useAdminTimesheet,
  useDeleteAdminEntry,
  useSignAdminTimesheet,
} from "@/hooks/useAdminTimesheet";
import { useApproveTimesheet } from "@/hooks/useApproval";
import type { TimesheetEntry, WorkType } from "@/api/admin-timesheet";

export interface TimesheetDayEditorProps {
  userId: number;
  date: string;
  /** Имя сотрудника для контекст-шапки. */
  name: string;
  /** Должность / табельный — вторичная строка шапки (если есть). */
  position?: string | null;
  /** ISO-понедельник недели — для точечного апдейта week-summary кэша. */
  weekStart: string;
  unitId: number | null;
  /** Поверхность: рулит density-токенами `--entry-tap-*` (§2.2). Контейнер
   *  (fullscreen ↔ Sheet) — на стороне хоста (`EntryDock`). */
  surface?: "mobile" | "desktop-dock";
  /** Self-ввод: субъект = сам актор. Тогда контекст-шапка «сотрудник · день»
   *  избыточна (идентичность и дата уже на левой панели «Заполнить время») — в
   *  доке оставляем только статус (UI-аудит 73 §2.3). */
  isSelf?: boolean;
  /** Закрыть оболочку по успешному финалу. */
  onFinalized?: () => void;
}

export function TimesheetDayEditor({
  userId,
  date,
  name,
  position,
  weekStart,
  unitId,
  surface = "desktop-dock",
  isSelf = false,
  onFinalized,
}: TimesheetDayEditorProps): React.ReactElement {
  const { t, i18n } = useTranslation();

  const snapshot = useAdminTimesheet(userId, date);
  const workTypes = useAdminEntryWorkTypes(userId);
  const deleteMut = useDeleteAdminEntry();
  const signMut = useSignAdminTimesheet();
  const approveMut = useApproveTimesheet();

  const [editor, setEditor] = React.useState<AdminEntryTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<TimesheetEntry | null>(null);

  // Свежий draft при смене сотрудника/дня — не тащим открытый редактор между
  // субъектами (эквивалент `key={userId}` старого композера). Сброс во время
  // рендера через стейт-«предыдущий ключ» («adjust state when a prop changes»,
  // React docs), не useEffect — иначе каскадный ререндер (правило react-hooks).
  const subjectKey = `${userId}:${date}`;
  const [lastSubjectKey, setLastSubjectKey] = React.useState(subjectKey);
  if (lastSubjectKey !== subjectKey) {
    setLastSubjectKey(subjectKey);
    setEditor(null);
  }

  const entries = snapshot.data?.entries ?? [];
  const sumHalf = snapshot.data?.sum_entries_hours_half ?? 0;
  const normHalf = snapshot.data?.default_declared_hours_half ?? 0;
  const status = snapshot.data?.timesheet.status ?? "draft";

  // Локальный табель: дефолт от бэка фиксируется лениво при первом готовом
  // снапшоте (компонент пересоздаётся по subjectKey — другой сотрудник/день
  // даёт свой инстанс через outer key хоста). До загрузки — null.
  const [declaredHalf, setDeclaredHalf] = React.useState<number | null>(null);
  const [syncedNorm, setSyncedNorm] = React.useState<number | null>(null);
  const [finalizeAttempted, setFinalizeAttempted] = React.useState(false);
  const [finalizeServerError, setFinalizeServerError] = React.useState<FormValidationIssue | null>(
    null,
  );
  if (snapshot.data && syncedNorm === null) {
    setSyncedNorm(normHalf);
    setDeclaredHalf(snapshot.data.timesheet.declared_total_hours_half);
  }
  useReleaseDirtyState(
    snapshot.data !== undefined &&
      declaredHalf !== snapshot.data.timesheet.declared_total_hours_half,
  );

  const finalizeIssue: FormValidationIssue | undefined =
    finalizeAttempted && (declaredHalf === null || declaredHalf < 0)
      ? {
          id: "day-editor-declared-error",
          message: t("validation.invalidValue"),
          kind: "field",
          controlId: "day-editor-declared-input",
        }
      : undefined;
  const finalizeIssues: FormValidationIssue[] = [
    ...(finalizeIssue ? [finalizeIssue] : []),
    ...(finalizeServerError ? [finalizeServerError] : []),
  ];
  const finalizing = isSelf ? signMut.isPending : approveMut.isPending;
  const canFinalize = !finalizing && !snapshot.isPending;

  const handleFinalize = (): void => {
    if (!canFinalize) return;
    setFinalizeServerError(null);
    if (declaredHalf === null || declaredHalf < 0) {
      setFinalizeAttempted(true);
      requestAnimationFrame(() => focusValidationControl("day-editor-declared-input"));
      return;
    }
    const callbacks = {
      onSuccess: () => {
        toast.success(t(isSelf ? "timeApproval.signedOk" : "timeApproval.enteredOk"));
        onFinalized?.();
      },
      onError: (err: Error) => {
        setFinalizeServerError(
          apiErrorFormIssue(err, {
            id: "day-editor-server-error",
            fallbackMessage: t("timeApproval.enterError"),
          }),
        );
        showApiErrorToast(err, { fallbackMessage: t("timeApproval.enterError") });
      },
    };
    if (isSelf) {
      signMut.mutate(
        { userId, date, payload: { declared_total_hours_half: declaredHalf } },
        callbacks,
      );
      return;
    }
    approveMut.mutate({ userId, date, weekStart, unitId, declaredHalf }, callbacks);
  };

  return (
    <>
      <div
        data-entry-surface={surface}
        data-testid="timesheet-day-editor"
        className="flex h-full flex-col"
      >
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
          {isSelf ? (
            // Self-ввод: субъект-эхо «сотрудник · день» дублирует левую панель
            // «Заполнить время» (идентичность + дата) — оставляем только статус
            // (UI-аудит 73 §2.3).
            <span
              className="font-condensed text-[10px] uppercase tracking-[0.18em] text-[var(--fg-3)]"
              data-testid="day-editor-status"
            >
              {position ? `${position} · ` : ""}
              {t(`admin.backfill.status.${statusKey(status)}`)}
            </span>
          ) : (
            <Field label={t("timeApproval.dockSubject")}>
              <div className="flex flex-col gap-0.5">
                <div className="font-semibold capitalize text-[var(--fg-1)]">
                  {name} · {formatWeekdayDate(date, i18n.resolvedLanguage)}
                </div>
                <span
                  className="font-condensed text-[10px] uppercase tracking-[0.18em] text-[var(--fg-3)]"
                  data-testid="day-editor-status"
                >
                  {position ? `${position} · ` : ""}
                  {t(`admin.backfill.status.${statusKey(status)}`)}
                </span>
              </div>
            </Field>
          )}

          <Field label={t("timeApproval.dockRecords")}>
            {snapshot.isPending ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-[var(--fg-3)]" aria-hidden />
              </div>
            ) : entries.length === 0 ? (
              <div
                className="rounded-[var(--radius-card)] border border-dashed border-[var(--rule-line)] px-3 py-3 text-center font-mono text-[11px] text-[var(--fg-4)]"
                data-testid="day-editor-no-records"
              >
                {t("timeApproval.dockNoRecords")}
              </div>
            ) : (
              <div className="flex flex-col">
                {entries.map((e) => (
                  <div
                    key={e.id}
                    data-testid={`day-editor-entry-${e.id}`}
                    className="flex items-center gap-2 border-b border-[var(--rule-faint)] py-1.5"
                  >
                    <button
                      type="button"
                      data-testid={`day-editor-entry-${e.id}-edit`}
                      aria-label={t("timeApproval.dockEditRecord")}
                      onClick={() => setEditor({ mode: "edit", entry: e })}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      style={{ minHeight: "var(--entry-tap-tertiary)" }}
                    >
                      <Pencil
                        className="h-3.5 w-3.5 shrink-0 self-start pt-0.5 text-[var(--fg-3)]"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 leading-snug">
                        <span
                          className="block truncate text-sm text-[var(--fg-1)]"
                          data-testid={`day-editor-entry-${e.id}-work`}
                        >
                          {workTypeName(workTypes, e.work_type_id) ?? e.work_type_text ?? "—"}
                        </span>
                        {e.assembly_element && (
                          <span
                            className="mt-0.5 block truncate text-[length:var(--fs-11)] text-[var(--fg-3)]"
                            data-testid={`day-editor-entry-${e.id}-element`}
                          >
                            {t("common.elementLabel", { name: e.assembly_element.name })}
                          </span>
                        )}
                        {entryProjectCode(e) && (
                          <span
                            className="mt-0.5 block truncate font-mono text-[11px] tabular-nums text-[var(--fg-3)]"
                            data-testid={`day-editor-entry-${e.id}-project`}
                          >
                            {entryProjectCode(e)}
                          </span>
                        )}
                        <ReworkBadge isRework={e.is_rework} note={e.rework_note} className="mt-1" />
                      </span>
                      <span className="shrink-0 self-start pt-0.5 font-mono text-sm font-semibold tabular-nums text-[var(--fg-1)]">
                        {formatHoursHalf(e.hours_half)}
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label={t("timeApproval.composerCancel")}
                      data-testid={`day-editor-entry-${e.id}-delete`}
                      disabled={deleteMut.isPending}
                      onClick={() => e.id != null && setDeleteTarget(e)}
                      className="inline-flex w-9 shrink-0 items-center justify-center rounded-[var(--radius-button)] border border-[var(--rule-line)] text-[var(--fg-3)]"
                      style={{ minHeight: "var(--entry-tap-tertiary)" }}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>

          {/* Крупная CTA в стиле мобильного AddEntryCta (FieldTimesheetPage):
              full-width, пунктир, иконка Plus, condensed-капс. Высота — CTA-токен
              --entry-tap-secondary (адаптируется: крупно на M6, компактно в доке).
              Прежний мелкий ghost-Button был почти не виден (85 §5.2). */}
          <button
            type="button"
            data-testid="day-editor-add-record"
            onClick={() => setEditor({ mode: "create" })}
            className="flex w-full items-center justify-center gap-2 rounded-[var(--radius-button)] border border-dashed border-[var(--rule-strong)] bg-[var(--bg-panel-0)] font-condensed text-[13px] font-semibold uppercase tracking-[0.14em] text-[var(--fg-1)] transition-colors hover:bg-[var(--bg-panel-1)]"
            style={{ minHeight: "var(--entry-tap-secondary)" }}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t("timeApproval.dockAddRecord")}
          </button>

          <Field label={t("timeApproval.dockDeclared")}>
            <DesktopHoursInput
              value={declaredHalf}
              onChange={setDeclaredHalf}
              minHours={0}
              maxHours={16}
              data-testid="day-editor-declared"
              aria-label={t("timeApproval.dockDeclared")}
              aria-invalid={Boolean(finalizeIssue)}
              aria-describedby={finalizeIssue ? finalizeIssue.id : undefined}
            />
            <FormFieldError issue={finalizeIssue} />
            {declaredHalf !== null && (
              <div className="mt-2">
                <OvertimeBanner
                  normHoursHalf={normHalf}
                  actualHoursHalf={declaredHalf}
                  showApprovalNote={false}
                  data-testid="day-editor-declared-warning"
                />
              </div>
            )}
          </Field>
        </div>

        <FormErrorSummary
          issues={finalizeIssues}
          countLabel={(count) => t("validation.fieldErrorCount", { count })}
          data-testid="day-editor-error-summary"
        />
        <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-[var(--rule-line)] bg-popover px-4 py-3">
          <span className="font-mono text-xs text-[var(--fg-2)]" data-testid="day-editor-tasks-sum">
            {t("timeApproval.compactTasks")} {formatHoursHalf(sumHalf)}
          </span>
          <Button
            data-testid="day-editor-confirm"
            disabled={!canFinalize}
            onClick={handleFinalize}
            style={{ minHeight: "var(--entry-tap-secondary)" }}
          >
            {finalizing
              ? t("timeApproval.dockSaving")
              : t(isSelf ? "timeApproval.dockSign" : "timeApproval.dockConfirm")}
          </Button>
        </div>
      </div>

      {/* Редактор записи — отдельный правый `<Sheet>`-слой поверх оболочки
          (тот же `surface="desktop-dock"`, что прежде в `EntryDock`/backfill).
          Admin-адаптер пишет запись + инвалидирует snapshot — список и сумма
          обновляются. Финал (approve+табель) — на «Подтвердить», не здесь. */}
      {editor !== null && (
        <AdminEntryEditor
          userId={userId}
          date={date}
          target={editor}
          myWorkTypes={workTypes}
          onClose={() => setEditor(null)}
        />
      )}
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("timeApproval.entryDeleteTitle")}
        body={t("timeApproval.entryDeleteBody", {
          work:
            workTypeName(workTypes, deleteTarget?.work_type_id) ??
            deleteTarget?.work_type_text ??
            "—",
          hours: formatHoursHalf(deleteTarget?.hours_half ?? 0),
          person: name,
        })}
        submitLabel={t("common.delete")}
        onConfirm={async () => {
          if (deleteTarget?.id == null) return;
          try {
            await deleteMut.mutateAsync({ userId, date, entryId: deleteTarget.id });
            toast.success(t("timeApproval.entryDeleted"));
            setDeleteTarget(null);
          } catch (err) {
            showApiErrorToast(err, { fallbackMessage: t("timeApproval.entryDeleteError") });
          }
        }}
        destructive
        pending={deleteMut.isPending}
        testId="day-editor-entry-delete-confirm"
      />
    </>
  );
}

function statusKey(status: string): "draft" | "signed" | "approved" {
  return status === "signed" || status === "approved" ? status : "draft";
}

function workTypeName(
  workTypes: readonly WorkType[],
  id: number | null | undefined,
): string | null {
  if (id == null) return null;
  return workTypes.find((w) => w.id === id)?.name ?? null;
}

/**
 * Код проекта записи (вторичная muted-строка). Для проекта из справочника —
 * «код · название» (паритет со сводкой рабочего `/field`); код/название
 * приходят обогащением admin-снапшота (§2.5). Если название пустое — только
 * код. Free-text ввод → `project_code_raw` как есть. Оба NULL (проект осознанно
 * пропущен) → null.
 */
function entryProjectCode(entry: TimesheetEntry): string | null {
  if (entry.project_code_raw) return entry.project_code_raw;
  if (entry.project_code) {
    return entry.project_name
      ? `${entry.project_code} · ${entry.project_name}`
      : entry.project_code;
  }
  return null;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-condensed text-[10px] uppercase tracking-[0.06em] text-[var(--fg-3)]">
        {label}
      </span>
      {children}
    </div>
  );
}
